import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  Switch,
  Typography
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DataSourceService from '../../../services/DataSourceService';
import SemanticTableService from '../../../services/SemanticTableService';
import useMetadataGenerationJob from '../../../hooks/useMetadataGenerationJob/useMetadataGenerationJob';

// Mismo azul de acento que el menú lateral de AdminHomeLayout.jsx (#38bdf8)
// y que ya usan SemanticTableFormDialog.js/TableSelectorDialog.js para esta
// misma familia de diálogos oscuros -- se reutiliza tal cual para que este
// wizard combine con el diseño ya trabajado, no uno nuevo.
const accentColor = 'var(--wb-accent)';
const labelColor = { color: 'var(--wb-text-label)' };
const inputSx = {
  color: 'var(--wb-text-strong)',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--wb-x-border-60a5fa4c, rgba(96, 165, 250, 0.3))' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: accentColor },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: accentColor }
};
const switchSx = {
  '& .MuiSwitch-switchBase': { color: 'var(--wb-accent)' },
  '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--wb-accent)' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'var(--wb-x-bg-38bdf8a6, rgba(56, 189, 248, 0.65))' },
  '& .MuiSwitch-track': { backgroundColor: 'var(--wb-blue-40)' }
};
const stepperSx = {
  '& .MuiStepLabel-label': { color: 'var(--wb-x-text-cbd5f599, rgba(203, 213, 245, 0.6))' },
  '& .MuiStepLabel-label.Mui-active': { color: 'var(--wb-text-strong)', fontWeight: 600 },
  '& .MuiStepLabel-label.Mui-completed': { color: accentColor },
  '& .MuiStepIcon-root': { color: 'var(--wb-x-text-60a5fa40, rgba(96, 165, 250, 0.25))' },
  '& .MuiStepIcon-root.Mui-active': { color: accentColor },
  '& .MuiStepIcon-root.Mui-completed': { color: accentColor }
};

const STEP_LABELS = ['Conexión', 'Tablas', 'Generación con IA', 'Progreso', 'Resumen'];

// Conexión de archivo (Excel/CSV, ver DataSourceService.mapApiTypeToUiType)
// -- sus tablas ya están resueltas en la propia conexión (sheet_progress),
// nunca requieren introspección de base de datos en vivo.
const isFileConnectionType = (uiType) => uiType === 'Excel';

/**
 * Ingesta multi-hoja: si sheet_progress existe, un semantic_table_payload
 * por hoja EXITOSA; si no (conexión de 1 sola hoja o de antes de esa
 * feature), cae al campo singular. Misma lógica que
 * SemanticModelProvisioningService.js::extractTablePayloads -- duplicada
 * acá a propósito (no está exportada, y este wizard es un módulo
 * independiente).
 */
function extractTablePayloadsFromConnection(connection) {
  if (Array.isArray(connection?.sheet_progress) && connection.sheet_progress.length > 0) {
    return connection.sheet_progress
      .filter((sheet) => sheet.status === 'success' && sheet.semantic_table_payload)
      .map((sheet) => JSON.parse(sheet.semantic_table_payload));
  }
  return connection?.semantic_table_payload ? [JSON.parse(connection.semantic_table_payload)] : [];
}

/** "Le faltan propiedades" -- sin descripción de tabla, o alguna columna sin descripción/nombre de negocio. */
function tableNeedsAIProperties(table) {
  if (!table.description || !table.display_name) {
    return true;
  }
  const columns = table.columns || [];
  return columns.some((c) => !c.description && !c.business_name);
}

/** Reduce columnas ya enriquecidas al shape de entrada que espera el modo "columns" de generateMetadata. */
function toBasicColumns(columns) {
  return (columns || []).map((c) => ({
    column_name: c.column_name,
    data_type: c.data_type || 'string',
    is_primary_key: Boolean(c.is_primary_key),
    nullable: c.nullable !== false
  }));
}

const initialRowStatus = { status: 'queued', errorMessage: null };

/**
 * Wizard de importación masiva de tablas al modelo semántico, desde
 * cualquier conexión (relacional en vivo, o Excel/CSV ya ingerido a
 * Iceberg). Reemplaza a TableSelectorDialog (que solo permitía elegir UNA
 * tabla, y para conexiones de archivo nunca desbloqueaba el paso de tablas
 * porque derivaba la "base de datos" de connection.databaseName -- vacío
 * siempre para Excel/CSV, ver handle_create_connection en
 * whisperbi-back-security: database_name queda '' para conexiones de
 * archivo). Acá, para conexiones de archivo, las tablas salen directo de
 * la propia conexión (sheet_progress) -- no hay "base de datos" que elegir.
 */
const ImportTablesWizard = ({ open, onClose, accountId, semanticDatabaseId, onImportComplete }) => {
  const [activeStep, setActiveStep] = useState(0);

  const [connections, setConnections] = useState([]);
  const [isLoadingConnections, setIsLoadingConnections] = useState(false);
  const [selectedConnectionId, setSelectedConnectionId] = useState('');
  const [loadError, setLoadError] = useState(null);

  // Solo para conexiones relacionales -- las de archivo no necesitan nada de esto.
  const [databases, setDatabases] = useState([]);
  const [selectedDatabase, setSelectedDatabase] = useState('');

  const [availableTables, setAvailableTables] = useState([]); // [{table_name, columns, ...payload}]
  const [isLoadingTables, setIsLoadingTables] = useState(false);
  const [selectedTableNames, setSelectedTableNames] = useState(new Set());

  const [generateAI, setGenerateAI] = useState(true);

  const [importRows, setImportRows] = useState([]); // [{table_name, status, errorMessage}]
  const [isImporting, setIsImporting] = useState(false);
  const { run: runMetadataGenerationJob } = useMetadataGenerationJob();

  const selectedConnection = connections.find((c) => c.id === selectedConnectionId) || null;
  const isFileConnection = isFileConnectionType(selectedConnection?.type);

  const resetAll = () => {
    setActiveStep(0);
    setSelectedConnectionId('');
    setDatabases([]);
    setSelectedDatabase('');
    setAvailableTables([]);
    setSelectedTableNames(new Set());
    setGenerateAI(true);
    setImportRows([]);
    setIsImporting(false);
    setLoadError(null);
  };

  const handleClose = () => {
    if (isImporting) return; // no cerrar a mitad de una importación en curso
    resetAll();
    onClose();
  };

  const loadConnections = useCallback(async () => {
    setIsLoadingConnections(true);
    setLoadError(null);
    try {
      const fetched = await DataSourceService.fetchDataSources();
      setConnections(fetched || []);
    } catch (err) {
      console.error('Error cargando conexiones:', err);
      setLoadError('No se pudieron cargar las conexiones: ' + err.message);
    } finally {
      setIsLoadingConnections(false);
    }
  }, []);

  const handleOpenStep1 = () => {
    if (connections.length === 0) {
      loadConnections();
    }
  };

  const handleConnectionChange = async (event) => {
    const connectionId = event.target.value;
    const connection = connections.find((c) => c.id === connectionId);
    setSelectedConnectionId(connectionId);
    setDatabases([]);
    setSelectedDatabase('');
    setAvailableTables([]);
    setSelectedTableNames(new Set());
    setLoadError(null);

    if (!connection) return;

    if (isFileConnectionType(connection.type)) {
      // Conexión de archivo -- las tablas ya están resueltas, se leen
      // directo de la conexión (ver docstring del módulo).
      setIsLoadingTables(true);
      try {
        const raw = await DataSourceService.getRawSourceConnection(connectionId);
        const payloads = extractTablePayloadsFromConnection(raw);
        setAvailableTables(payloads);
        if (payloads.length === 0) {
          setLoadError('Esta conexión todavía no tiene tablas ingeridas (¿la carga sigue en curso o falló?).');
        }
      } catch (err) {
        console.error('Error cargando tablas de la conexión:', err);
        setLoadError('No se pudieron cargar las tablas de esta conexión: ' + err.message);
      } finally {
        setIsLoadingTables(false);
      }
    } else if (connection.databaseName) {
      setDatabases([connection.databaseName]);
    }
  };

  const loadRelationalTables = useCallback(
    async (database) => {
      if (!selectedConnection || !database) return;
      setIsLoadingTables(true);
      setLoadError(null);
      try {
        const tables = await SemanticTableService.listDatabaseTables(selectedConnection.id, database, null);
        // Tablas relacionales: solo se conoce el nombre acá -- las columnas
        // recién se resuelven en el paso de progreso (generateMetadata modo
        // "connection", introspección en vivo, una tabla a la vez).
        setAvailableTables(
          (tables || []).map((t) => ({
            table_name: t.table_name,
            database,
            connection_id: selectedConnection.id,
            columns: null
          }))
        );
      } catch (err) {
        console.error('Error cargando tablas:', err);
        setLoadError('No se pudieron cargar las tablas: ' + err.message);
        setAvailableTables([]);
      } finally {
        setIsLoadingTables(false);
      }
    },
    [selectedConnection]
  );

  const handleDatabaseChange = (event) => {
    const database = event.target.value;
    setSelectedDatabase(database);
    setSelectedTableNames(new Set());
    loadRelationalTables(database);
  };

  const toggleTable = (tableName) => {
    setSelectedTableNames((prev) => {
      const next = new Set(prev);
      if (next.has(tableName)) {
        next.delete(tableName);
      } else {
        next.add(tableName);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedTableNames((prev) =>
      prev.size === availableTables.length ? new Set() : new Set(availableTables.map((t) => t.table_name))
    );
  };

  const canGoToStep2 = Boolean(selectedConnectionId) && !loadError;
  const canGoToStep3 = selectedTableNames.size > 0;

  const handleStartImport = async () => {
    setActiveStep(3);
    setIsImporting(true);

    const selected = availableTables.filter((t) => selectedTableNames.has(t.table_name));
    setImportRows(selected.map((t) => ({ table_name: t.table_name, ...initialRowStatus })));

    const updateRow = (tableName, patch) => {
      setImportRows((prev) => prev.map((r) => (r.table_name === tableName ? { ...r, ...patch } : r)));
    };

    // Existentes de esta base semántica -- evita duplicar si la tabla ya
    // fue importada antes (mismo find-or-create que
    // SemanticModelProvisioningService.provisionFromConnection).
    let existingTableList = [];
    try {
      const existing = await SemanticTableService.list({ accountId, semanticDatabaseId });
      existingTableList = Array.isArray(existing) ? existing : existing?.items || existing?.data || [];
    } catch (err) {
      console.error('No se pudo revisar tablas existentes (se intentará crear igual):', err);
    }

    // Una por una, a propósito -- correr todas en paralelo saturaría el
    // modelo de IA (Bedrock) con N llamadas simultáneas.
    for (const table of selected) {
      // eslint-disable-next-line no-await-in-loop
      try {
        updateRow(table.table_name, { status: 'preparando' });

        let finalPayload = { ...table, account_id: accountId, semantic_database_id: semanticDatabaseId };

        const needsAI = generateAI && (isFileConnection ? tableNeedsAIProperties(table) : true);

        if (needsAI) {
          updateRow(table.table_name, { status: 'generando_ia' });
          const generationPayload = isFileConnection
            ? {
                table_name: table.table_name,
                columns: toBasicColumns(table.columns),
                semantic_database_id: semanticDatabaseId,
                generate_ai_descriptions: true,
                language: 'es'
              }
            : {
                connection_id: selectedConnection.id,
                database: selectedDatabase,
                table_name: table.table_name,
                generate_ai_descriptions: true,
                language: 'es'
              };

          // eslint-disable-next-line no-await-in-loop
          const generated = await runMetadataGenerationJob(generationPayload);
          if (!generated) {
            throw new Error('No se pudo generar la metadata con IA para esta tabla.');
          }
          finalPayload = {
            ...finalPayload,
            display_name: generated.display_name || finalPayload.display_name,
            description: generated.description || finalPayload.description,
            columns: generated.columns || finalPayload.columns,
            database: finalPayload.database || generated.database,
            schema: finalPayload.schema || generated.schema || 'default',
            sql_dialect: finalPayload.sql_dialect || generated.sql_dialect,
            connection_id: finalPayload.connection_id || selectedConnection.id
          };
        } else if (!isFileConnection) {
          // Relacional sin IA -- igual hace falta la introspección en vivo
          // para conocer las columnas (no hay otra fuente para eso).
          updateRow(table.table_name, { status: 'leyendo_columnas' });
          const generationPayload = {
            connection_id: selectedConnection.id,
            database: selectedDatabase,
            table_name: table.table_name,
            generate_ai_descriptions: false
          };
          // eslint-disable-next-line no-await-in-loop
          const generated = await runMetadataGenerationJob(generationPayload);
          if (!generated) {
            throw new Error('No se pudieron leer las columnas de esta tabla.');
          }
          finalPayload = {
            ...finalPayload,
            display_name: generated.display_name || finalPayload.display_name,
            description: generated.description || finalPayload.description,
            columns: generated.columns || finalPayload.columns,
            database: finalPayload.database || generated.database,
            schema: finalPayload.schema || generated.schema || 'default',
            sql_dialect: finalPayload.sql_dialect || generated.sql_dialect,
            connection_id: finalPayload.connection_id || selectedConnection.id
          };
        }

        if (!Array.isArray(finalPayload.columns) || finalPayload.columns.length === 0) {
          throw new Error('La tabla no tiene columnas conocidas.');
        }

        updateRow(table.table_name, { status: 'creando' });

        const existingMatch = existingTableList.find(
          (t) =>
            t.table_name === table.table_name &&
            (t.connection_id === finalPayload.connection_id || !finalPayload.connection_id)
        );

        if (existingMatch) {
          const tableId = existingMatch.table_id || existingMatch.id;
          // eslint-disable-next-line no-await-in-loop
          await SemanticTableService.update(tableId, finalPayload);
        } else {
          // eslint-disable-next-line no-await-in-loop
          await SemanticTableService.create(finalPayload);
        }

        updateRow(table.table_name, { status: 'success' });
      } catch (err) {
        console.error(`Error importando la tabla ${table.table_name}:`, err);
        updateRow(table.table_name, { status: 'error', errorMessage: err.message || 'Error desconocido' });
      }
    }

    setIsImporting(false);
    setActiveStep(4);
    onImportComplete?.();
  };

  const rowStatusMeta = {
    queued: { label: 'En cola', color: 'var(--wb-x-text-cbd5f599, rgba(203, 213, 245, 0.6))' },
    preparando: { label: 'Preparando…', color: accentColor },
    leyendo_columnas: { label: 'Leyendo columnas…', color: accentColor },
    generando_ia: { label: 'Generando con IA…', color: accentColor },
    creando: { label: 'Creando entidad…', color: accentColor },
    success: { label: 'Importada', color: 'var(--wb-success)' },
    error: { label: 'Error', color: 'var(--wb-danger)' }
  };

  const successCount = importRows.filter((r) => r.status === 'success').length;
  const errorRows = importRows.filter((r) => r.status === 'error');

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      TransitionProps={{ onEnter: handleOpenStep1 }}
      PaperProps={{
        sx: {
          bgcolor: 'var(--wb-bg-surface)',
          color: 'var(--wb-text-strong)',
          borderRadius: 0,
          border: '1px solid var(--wb-x-border-60a5fa26, rgba(96, 165, 250, 0.15))'
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid var(--wb-border)' }}>
        Importar tablas al modelo semántico
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 3, ...stepperSx }}>
          {STEP_LABELS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {loadError && (
          <Alert
            severity="error"
            onClose={() => setLoadError(null)}
            sx={{ mb: 2, bgcolor: 'var(--wb-x-bg-ef44441a, rgba(239, 68, 68, 0.1))', color: 'var(--wb-danger-light)', '& .MuiAlert-icon': { color: 'var(--wb-x-text-ef4444, #ef4444)' } }}
          >
            {loadError}
          </Alert>
        )}

        {activeStep === 0 && (
          <Box>
            {isLoadingConnections ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <CircularProgress size={22} sx={{ color: accentColor }} />
                <Typography sx={{ color: 'var(--wb-text-label)' }}>Cargando conexiones…</Typography>
              </Box>
            ) : (
              <FormControl fullWidth>
                <InputLabel sx={labelColor}>Conexión</InputLabel>
                <Select value={selectedConnectionId} onChange={handleConnectionChange} label="Conexión" sx={inputSx}>
                  {connections.map((conn) => (
                    <MenuItem key={conn.id} value={conn.id}>
                      {conn.name} ({conn.type})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {!isFileConnection && selectedConnection && databases.length > 0 && (
              <FormControl fullWidth sx={{ mt: 3 }}>
                <InputLabel sx={labelColor}>Base de datos</InputLabel>
                <Select value={selectedDatabase} onChange={handleDatabaseChange} label="Base de datos" sx={inputSx}>
                  {databases.map((db) => (
                    <MenuItem key={db} value={db}>
                      {db}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {isLoadingTables && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                <CircularProgress size={20} sx={{ color: accentColor }} />
                <Typography sx={{ color: 'var(--wb-text-label)' }}>Cargando tablas disponibles…</Typography>
              </Box>
            )}
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={availableTables.length > 0 && selectedTableNames.size === availableTables.length}
                  indeterminate={selectedTableNames.size > 0 && selectedTableNames.size < availableTables.length}
                  onChange={toggleSelectAll}
                  sx={{ color: accentColor, '&.Mui-checked': { color: accentColor } }}
                />
              }
              label="Seleccionar todas"
              sx={{ color: 'var(--wb-text-label)', mb: 1 }}
            />
            <List dense sx={{ maxHeight: 360, overflow: 'auto', border: '1px solid var(--wb-border)', borderRadius: 1 }}>
              {availableTables.map((table) => (
                <ListItem key={table.table_name} button onClick={() => toggleTable(table.table_name)}>
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={selectedTableNames.has(table.table_name)}
                      tabIndex={-1}
                      disableRipple
                      sx={{ color: accentColor, '&.Mui-checked': { color: accentColor } }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography sx={{ color: 'var(--wb-text-strong)' }}>{table.table_name}</Typography>}
                    secondary={
                      <Typography variant="caption" sx={{ color: 'var(--wb-x-text-cbd5f5b2, rgba(203, 213, 245, 0.7))' }}>
                        {Array.isArray(table.columns) ? `${table.columns.length} columnas` : 'Columnas por leer'}
                        {table.description ? ` · ${table.description}` : ''}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
              {availableTables.length === 0 && (
                <Typography sx={{ color: 'var(--wb-x-text-cbd5f5b2, rgba(203, 213, 245, 0.7))', p: 2 }}>
                  No hay tablas disponibles para esta conexión.
                </Typography>
              )}
            </List>
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            <FormControlLabel
              control={
                <Switch checked={generateAI} onChange={(e) => setGenerateAI(e.target.checked)} sx={switchSx} />
              }
              label="Generar con IA las propiedades faltantes"
              sx={{ color: 'var(--wb-text-label)' }}
            />
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'var(--wb-x-text-cbd5f5b2, rgba(203, 213, 245, 0.7))' }}>
              {isFileConnection
                ? 'Solo se llama a la IA para las tablas a las que les falte descripción o alguna propiedad de columna -- las que ya la tienen (generada al subir el archivo) se importan tal cual.'
                : 'Se usa para describir cada tabla y sus columnas al importarlas. Puede tardar unos minutos por tabla.'}
            </Typography>
          </Box>
        )}

        {activeStep === 3 && (
          <Box>
            <List dense>
              {importRows.map((row) => {
                const meta = rowStatusMeta[row.status] || rowStatusMeta.queued;
                return (
                  <ListItem key={row.table_name} sx={{ alignItems: 'flex-start' }}>
                    <ListItemText
                      sx={{ flex: '0 0 40%' }}
                      primary={<Typography sx={{ color: 'var(--wb-text-strong)' }}>{row.table_name}</Typography>}
                      secondary={
                        row.errorMessage ? (
                          <Typography variant="caption" sx={{ color: 'var(--wb-danger-light)' }}>
                            {row.errorMessage}
                          </Typography>
                        ) : null
                      }
                    />
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      {row.status === 'success' ? (
                        <CheckCircleIcon sx={{ color: 'var(--wb-success)', fontSize: 20 }} />
                      ) : row.status === 'error' ? (
                        <ErrorIcon sx={{ color: 'var(--wb-danger)', fontSize: 20 }} />
                      ) : row.status === 'queued' ? null : (
                        <LinearProgress sx={{ flex: 1, bgcolor: 'var(--wb-x-bg-60a5fa26, rgba(96, 165, 250, 0.15))' }} />
                      )}
                      <Typography variant="caption" sx={{ color: meta.color }}>
                        {meta.label}
                      </Typography>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}

        {activeStep === 4 && (
          <Box>
            <Alert
              severity={errorRows.length > 0 ? 'warning' : 'success'}
              sx={
                errorRows.length > 0
                  ? { bgcolor: 'var(--wb-x-bg-fbbf241a, rgba(251, 191, 36, 0.1))', color: 'var(--wb-x-text-fcd34d, #fcd34d)', '& .MuiAlert-icon': { color: 'var(--wb-x-text-f59e0b, #f59e0b)' } }
                  : { bgcolor: 'var(--wb-x-bg-4ade801a, rgba(74, 222, 128, 0.1))', color: 'var(--wb-x-text-bbf7d0, #bbf7d0)', '& .MuiAlert-icon': { color: 'var(--wb-success)' } }
              }
            >
              {successCount} de {importRows.length} tabla(s) importadas correctamente.
            </Alert>
            {errorRows.length > 0 && (
              <List dense sx={{ mt: 1 }}>
                {errorRows.map((row) => (
                  <ListItem key={row.table_name}>
                    <ListItemText
                      primary={<Typography sx={{ color: 'var(--wb-text-strong)' }}>{row.table_name}</Typography>}
                      secondary={
                        <Typography variant="caption" sx={{ color: 'var(--wb-danger-light)' }}>
                          {row.errorMessage}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid var(--wb-border)', p: 2.5 }}>
        {activeStep === 0 && (
          <>
            <Button onClick={handleClose} sx={{ color: 'var(--wb-text-label)' }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={!canGoToStep2 || isLoadingTables || availableTables.length === 0}
              onClick={() => setActiveStep(1)}
              sx={{ bgcolor: accentColor, '&:hover': { bgcolor: 'var(--wb-sky)' }, '&:disabled': { bgcolor: 'var(--wb-accent-30)', color: 'var(--wb-text-dim)' } }}
            >
              Siguiente
            </Button>
          </>
        )}
        {activeStep === 1 && (
          <>
            <Button onClick={() => setActiveStep(0)} sx={{ color: 'var(--wb-text-label)' }}>
              Atrás
            </Button>
            <Button
              variant="contained"
              disabled={!canGoToStep3}
              onClick={() => setActiveStep(2)}
              sx={{ bgcolor: accentColor, '&:hover': { bgcolor: 'var(--wb-sky)' }, '&:disabled': { bgcolor: 'var(--wb-accent-30)', color: 'var(--wb-text-dim)' } }}
            >
              Siguiente
            </Button>
          </>
        )}
        {activeStep === 2 && (
          <>
            <Button onClick={() => setActiveStep(1)} sx={{ color: 'var(--wb-text-label)' }}>
              Atrás
            </Button>
            <Button
              variant="contained"
              onClick={handleStartImport}
              sx={{ bgcolor: accentColor, '&:hover': { bgcolor: 'var(--wb-sky)' } }}
            >
              Iniciar importación
            </Button>
          </>
        )}
        {activeStep === 3 && (
          <Button disabled={isImporting} sx={{ color: 'var(--wb-text-label)' }}>
            {isImporting ? 'Importando…' : 'Procesando…'}
          </Button>
        )}
        {activeStep === 4 && (
          <Button variant="contained" onClick={handleClose} sx={{ bgcolor: accentColor, '&:hover': { bgcolor: 'var(--wb-sky)' } }}>
            Cerrar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportTablesWizard;
